import React, { useCallback } from 'react';
import {
  Stat,
  StatProps,
  StatNumber,
  StatLabel,
  useControllableState,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { QColorVariant } from '../../QTypes';
import { QIcon, QFlex, QBox } from '../..';
import { IconNameType } from '../../types/Icons';

export interface QStatProps extends Pick<StatProps, 'variant'> {
  data: React.ReactNode;
  iconName?: IconNameType;
  label: React.ReactNode;
  isSelected?: boolean;
  onChange?: () => void;
  variant?:
    | QColorVariant
    | 'outline'
    | 'ghost'
    | `outline-${QColorVariant}`
    | `ghost-${QColorVariant}`;
  isInteractive?: boolean;
  bottomHeavy?: boolean;
}

export const QStat = React.forwardRef<HTMLDivElement, QStatProps>(
  (
    {
      data,
      iconName,
      label,
      variant: rawVariant,
      isSelected,
      isInteractive,
      onChange,
      bottomHeavy,
      ...rest
    },
    ref,
  ) => {
    const [selected, setSelected] = useControllableState({
      value: isSelected,
      onChange,
    });

    const [variant, iconColor] = parseVariant(rawVariant);

    const styles = useMultiStyleConfig('Stat', { variant });

    const toggleSelected = useCallback(
      () => setSelected((s) => !s),
      [setSelected],
    );

    const handleClick = isInteractive ? toggleSelected : undefined;

    if (bottomHeavy) {
      return (
        <Stat
          ref={ref}
          aria-selected={selected}
          variant={variant}
          onClick={handleClick}
          role={isInteractive ? 'button' : undefined}
          sx={isInteractive ? styles.interactive : {}}
          overflow={variant == 'ghost' ? undefined : 'hidden'}
          {...rest}
        >
          <StatLabel>{label}</StatLabel>
          <StatNumber fontSize="2xl" mt={4}>
            {data}
          </StatNumber>
          {iconName && (
            <QBox position="absolute" right="0px" bottom="-20px" opacity={0.2}>
              <QIcon iconName={iconName} size="6xl" color={iconColor} />
            </QBox>
          )}
        </Stat>
      );
    }

    return (
      <Stat
        ref={ref}
        aria-selected={selected}
        variant={variant}
        onClick={handleClick}
        role={isInteractive ? 'button' : undefined}
        sx={isInteractive ? styles.interactive : {}}
        {...rest}
      >
        <QFlex>
          {iconName && (
            <QFlex marginTop={2} marginRight={2}>
              <QIcon iconName={iconName} color={iconColor} />
            </QFlex>
          )}
          <QFlex direction="column">
            <StatNumber>{data}</StatNumber>
            <StatLabel>{label}</StatLabel>
          </QFlex>
        </QFlex>
      </Stat>
    );
  },
);

QStat.displayName = 'QStat';

const parseVariant = (variant: QStatProps['variant']) => {
  const match = !!variant && /(.+)-(.+)/.exec(variant);
  if (match) {
    return [match[1], match[2]];
  }
  return [variant, undefined];
};
